import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, Search, Users, UserCheck, UserPlus, DollarSign, AlertTriangle, X, Mail,
  Tag, StickyNote, Trash2, Calendar, ShoppingBag, HeartHandshake, MessageSquare, Star, Send,
  Download, Bookmark, BookmarkPlus, Clock, Repeat, Eye, MousePointer, Zap, Pencil, Phone, PhoneCall, MessageCircle, Archive, ArchiveRestore, MailWarning, Sparkles,
} from 'lucide-react';
import api, { mediaUrl } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import RichEmailEditor, { richTextIsEmpty } from '../../components/RichEmailEditor';
import ContactImportModal from '../../components/ContactImportModal';
import BounceManager from '../../components/BounceManager';
import { VoiceRecorder } from '../../components/VoiceRecorder';

const sym = (c) => (c === 'inr' ? '₹' : '$');
const fmt = (iso) => { try { return iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—'; } catch { return iso; } };
const dispEmail = (e) => (e && e.endsWith('@osho.local') ? '' : e);
const TIER_LABELS = { basic: 'Seekers Community', gold: 'Yearly Gold', diamond: '5-Year Diamond', lifetime: 'Lifetime Residential' };

const SEGMENTS = [
  ['', 'All'], ['members', 'Members'], ['account', 'Registered'], ['guest', 'Guests'], ['lapsed', 'Lapsed'], ['needs_attention', 'Needs attention'],
];

const TYPE_ICON = { booking: Calendar, order: ShoppingBag, donation: HeartHandshake, testimonial: Star, message: MessageSquare, call: PhoneCall };
const CHANNEL_ICON = { email: Mail, sms: MessageSquare, whatsapp: MessageCircle };
const CHANNEL_LABEL = { email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp' };
const STATUS_STYLE = {
  paid: 'bg-green-100 text-green-800', free: 'bg-mahogany/10 text-mahogany', pending: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800', sent: 'bg-green-100 text-green-800', published: 'bg-green-100 text-green-800',
  refunded: 'bg-mahogany/15 text-mahogany',
};

const skipReasons = (j) => {
  const r = [j.skipped_optout && `${j.skipped_optout} opted out`, j.skipped_recent && `${j.skipped_recent} messaged in last 24h`].filter(Boolean);
  return r.length ? ` (${r.join(', ')})` : '';
};


const AdminCustomers = () => {
  const { toast } = useToast();
  const [data, setData] = useState({ customers: [], summary: {}, all_tags: [] });
  const [loading, setLoading] = useState(true);
  const [tierLabels, setTierLabels] = useState(TIER_LABELS);
  useEffect(() => {
    api.get('/admin/memberships/plans')
      .then((r) => setTierLabels({ ...TIER_LABELS, ...Object.fromEntries((r.data || []).map((p) => [p.tier_key, p.name])) }))
      .catch(() => {});
  }, []);
  const [q, setQ] = useState('');
  const [segment, setSegment] = useState('');
  const [tag, setTag] = useState('');
  const [country, setCountry] = useState('');
  const [stateF, setStateF] = useState('');
  const [city, setCity] = useState('');
  const [statusF, setStatusF] = useState(''); // acct_active | acct_deactivated | mem_active | mem_inactive
  const [selected, setSelected] = useState(() => new Set());
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const open = params.get('open');
    if (open) setOpenKey(open);
  }, []);
  const [segments, setSegments] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [repeatEvery, setRepeatEvery] = useState('none');
  const [scheduling, setScheduling] = useState(false);
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  const [scheduledList, setScheduledList] = useState([]);
  const [jobsList, setJobsList] = useState([]);
  const [engagement, setEngagement] = useState({});
  const [autoOpen, setAutoOpen] = useState(false);
  const [winback, setWinback] = useState(null);
  const [savingWb, setSavingWb] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkChannel, setBulkChannel] = useState('email');
  const [bulkPlain, setBulkPlain] = useState('');
  const [waTemplates, setWaTemplates] = useState([]);
  const [waTemplate, setWaTemplate] = useState('');
  const [waVars, setWaVars] = useState([]);
  const [waEvent, setWaEvent] = useState('');
  const [waHeaderUrl, setWaHeaderUrl] = useState('');
  const [msgConfig, setMsgConfig] = useState({ email: true, sms: false, whatsapp: false });
  const [deleting, setDeleting] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [eventOptions, setEventOptions] = useState([]);
  const [bouncesOpen, setBouncesOpen] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState(() => new Set());

  useEffect(() => {
    if (bulkOpen && eventOptions.length === 0) {
      api.get('/admin/crm/event-options').then((r) => setEventOptions(r.data || [])).catch(() => {});
    }
  }, [bulkOpen]); // eslint-disable-line

  const toggleEvent = (id) => setSelectedEvents((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const applyInviteTemplate = () => {
    if (bulkChannel === 'email') {
      setBulkSubject("You're invited, {{name}} 🌸");
      setBulkMessage("<p>Dear {{name}},</p><p>We would love to welcome you to our upcoming gatherings — come sit with us, breathe, and celebrate presence together.</p><p>Take a look below and reserve your place. <b>Register today</b> — spaces are limited.</p><p>With love and awareness,<br/>The Osho Universal team</p>");
    } else {
      setBulkPlain("Namaste {{name}} 🌸 You're invited to our upcoming gatherings at Osho Universal. Register today — details below:");
    }
  };

  const archiveContacts = async (emails, archived) => {
    if (!emails.length) return;
    try {
      const res = await api.post('/admin/crm/contacts/archive', { emails, archived });
      const sk = (res.data.skipped || []).length;
      toast({ title: `${archived ? 'Archived' : 'Restored'} ${res.data.changed} contact(s)`, description: sk ? `${sk} skipped (has an account).` : '' });
      setSelected(new Set());
      fetchCustomers();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
  };
  const [tagOpen, setTagOpen] = useState(false);
  const [addTags, setAddTags] = useState('');
  const [removeTags, setRemoveTags] = useState('');
  const [taggingBusy, setTaggingBusy] = useState(false);

  const applyBulkTags = async () => {
    const add = addTags.split(',').map((t) => t.trim()).filter(Boolean);
    const remove = removeTags.split(',').map((t) => t.trim()).filter(Boolean);
    if (!add.length && !remove.length) { toast({ title: 'Add or remove at least one tag.' }); return; }
    setTaggingBusy(true);
    try {
      const res = await api.post('/admin/crm/tags/bulk', { emails: Array.from(selected), add, remove });
      toast({ title: `Tagged ${res.data.updated} contact(s)`, description: [add.length ? `+${add.join(', ')}` : '', remove.length ? `−${remove.join(', ')}` : ''].filter(Boolean).join(' · ') });
      setTagOpen(false); setAddTags(''); setRemoveTags('');
      fetchCustomers();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setTaggingBusy(false); }
  };

  const deleteContacts = async (emails) => {
    if (!emails.length) return;
    if (!window.confirm(`Delete ${emails.length} imported contact(s)? This can't be undone. Contacts with an account or any transactions are skipped automatically.`)) return;
    setDeleting(true);
    try {
      const res = await api.post('/admin/crm/contacts/delete', { emails });
      const sk = (res.data.skipped || []).length;
      toast({ title: `Deleted ${res.data.deleted} contact(s)`, description: sk ? `${sk} skipped (has an account or transactions).` : 'Bad rows cleaned up.' });
      setSelected(new Set());
      fetchCustomers();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setDeleting(false); }
  };

  const deleteUsers = async (userIds) => {
    if (!userIds.length) return;
    if (!window.confirm(`Delete ${userIds.length} registered user(s) and free their seats? Their orders, bookings, payments and donations will be archived and removed from all lists. Admins and your own account are skipped.`)) return;
    setDeleting(true);
    try {
      const res = await api.post('/admin/users/bulk-delete', { user_ids: userIds });
      const sk = (res.data.skipped || []).length;
      toast({ title: `Deleted ${res.data.deleted} user(s)`, description: [res.data.seats_freed ? `${res.data.seats_freed} seats freed` : '', sk ? `${sk} skipped (admin/own)` : ''].filter(Boolean).join(' · ') || 'Records archived.' });
      setSelected(new Set());
      fetchCustomers();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setDeleting(false); }
  };

  useEffect(() => {
    api.get('/admin/crm/messaging-config').then((r) => setMsgConfig(r.data || {})).catch(() => {});
  }, []);

  // Load approved WhatsApp templates when the WhatsApp channel is first chosen.
  useEffect(() => {
    if (bulkChannel === 'whatsapp' && waTemplates.length === 0) {
      api.get('/admin/crm/whatsapp/templates').then((r) => {
        const list = r.data?.templates || [];
        setWaTemplates(list);
        const def = list.find((t) => t.name === 'osho_broadcast') || list[0];
        if (def) { setWaTemplate(def.name); setWaVars(Array(def.variables).fill('')); }
      }).catch(() => {});
    }
  }, [bulkChannel]);  // eslint-disable-line react-hooks/exhaustive-deps

  const selectedWaTemplate = waTemplates.find((t) => t.name === waTemplate) || null;
  const onPickTemplate = (name) => {
    setWaTemplate(name);
    const t = waTemplates.find((x) => x.name === name);
    setWaVars(Array(t?.variables || 0).fill(''));
    setWaHeaderUrl('');
  };
  const setWaVar = (i, v) => setWaVars((prev) => { const n = [...prev]; n[i] = v; return n; });
  const selectedWaEvent = eventOptions.find((e) => e.id === waEvent) || null;
  const onPickEvent = (id) => {
    setWaEvent(id);
    const ev = eventOptions.find((e) => e.id === id);
    if (ev && selectedWaTemplate?.header_format === 'IMAGE' && ev.image && !waHeaderUrl) {
      setWaHeaderUrl(mediaUrl(ev.image));
    }
  };
  const fillAllFromEvent = () => {
    if (!selectedWaEvent || !selectedWaTemplate) return;
    const seq = ['{{name}}', selectedWaEvent.title || '', selectedWaEvent.details || '', selectedWaEvent.register_url || ''];
    setWaVars(Array.from({ length: selectedWaTemplate.variables }).map((_, i) => seq[i] || ''));
    if (selectedWaTemplate.header_format === 'IMAGE' && selectedWaEvent.image) {
      setWaHeaderUrl(mediaUrl(selectedWaEvent.image));
    }
  };
  // Render the template body with {{n}} replaced by the admin's input (for a live preview).
  const waPreview = () => {
    if (!selectedWaTemplate) return '';
    return selectedWaTemplate.body.replace(/{{\s*(\d+)\s*}}/g, (_, n) => {
      const val = waVars[Number(n) - 1];
      return val && val.trim() ? val : `〈var ${n}〉`;
    });
  };

  const loadCampaigns = async () => {
    setCampaignsOpen(true);
    try {
      const [sc, jb] = await Promise.all([api.get('/admin/crm/scheduled'), api.get('/admin/crm/send-jobs')]);
      setScheduledList(sc.data || []); setJobsList(jb.data || []);
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const cancelScheduled = async (id) => {
    try { await api.delete(`/admin/crm/scheduled/${id}`); loadCampaigns(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const loadEngagement = async (jobId) => {
    if (engagement[jobId]) { setEngagement((p) => ({ ...p, [jobId]: { ...p[jobId], open: !p[jobId].open } })); return; }
    try { const res = await api.get(`/admin/crm/send-jobs/${jobId}/engagement`); setEngagement((p) => ({ ...p, [jobId]: { ...res.data, open: true } })); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const scheduleCampaign = async () => {
    if (bulkSubject.trim().length < 1 || richTextIsEmpty(bulkMessage)) { toast({ title: 'Add a subject and message.' }); return; }
    if (!scheduleAt) { toast({ title: 'Pick a date & time to schedule.' }); return; }
    setScheduling(true);
    try {
      await api.post('/admin/crm/scheduled', { subject: bulkSubject, message: bulkMessage, send_at: new Date(scheduleAt).toISOString(), repeat: repeatEvery, event_ids: Array.from(selectedEvents), ...activeFilters() });
      toast({ title: repeatEvery === 'none' ? 'Campaign scheduled' : `Recurring campaign scheduled (${repeatEvery})`, description: new Date(scheduleAt).toLocaleString() });
      setBulkOpen(false); setBulkSubject(''); setBulkMessage(''); setScheduleAt(''); setRepeatEvery('none'); setSelectedEvents(new Set());
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setScheduling(false); }
  };

  const openAutomations = async () => {
    setAutoOpen(true);
    try { const res = await api.get('/admin/crm/settings/winback'); setWinback(res.data); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const saveWinback = async () => {
    setSavingWb(true);
    try {
      const res = await api.put('/admin/crm/settings/winback', { enabled: winback.enabled, days: Number(winback.days) || 180, subject: winback.subject, message: winback.message });
      setWinback(res.data); toast({ title: 'Winback settings saved' });
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
    finally { setSavingWb(false); }
  };
  const runWinbackNow = async () => {
    try { const res = await api.post('/admin/crm/winback/run'); toast({ title: 'Winback run', description: `${res.data.sent || 0} sent · ${res.data.skipped || 0} skipped` }); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };

  const fetchSegments = async () => {
    try { const res = await api.get('/admin/crm/segments'); setSegments(res.data || []); }
    catch (e) { console.error('Failed to load segments:', e); }
  };
  useEffect(() => { fetchSegments(); }, []);

  const saveSegment = async () => {
    const name = window.prompt('Name this segment (e.g. "VIP donors"):');
    if (!name) return;
    try { await api.post('/admin/crm/segments', { name, q, segment, tag, country, state: stateF, city, ...statusParams(), status_raw: statusF }); toast({ title: 'Segment saved' }); fetchSegments(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const deleteSegment = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/admin/crm/segments/${id}`); fetchSegments(); }
    catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail }); }
  };
  const applySegment = (sg) => {
    setQ(sg.q || ''); setSegment(sg.segment || ''); setTag(sg.tag || '');
    setCountry(sg.country || ''); setStateF(sg.state || ''); setCity(sg.city || '');
    setStatusF(sg.status_raw || '');
  };

  const exportCsv = async () => {
    try {
      const res = await api.get('/admin/crm/export/customers.csv', { params: activeFilters(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'osho_customers.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'Export failed', description: e?.response?.data?.detail }); }
  };

  const sendBulk = async () => {
    const isEmail = bulkChannel === 'email';
    const isWa = bulkChannel === 'whatsapp';
    if (isEmail && (bulkSubject.trim().length < 1 || richTextIsEmpty(bulkMessage))) { toast({ title: 'Add a subject and message.' }); return; }
    if (isWa) {
      if (!waTemplate) { toast({ title: 'Pick a WhatsApp template.' }); return; }
      if ((selectedWaTemplate?.variables || 0) > 0 && waVars.some((v) => !v || !v.trim())) {
        toast({ title: 'Fill in every template variable.' }); return;
      }
      if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedWaTemplate?.header_format) && !waHeaderUrl.trim()) {
        toast({ title: `Add a header ${selectedWaTemplate.header_format.toLowerCase()} URL.` }); return;
      }
    }
    if (!isEmail && !isWa && bulkPlain.trim().length < 1) { toast({ title: 'Write a message.' }); return; }
    setBulkSending(true);
    try {
      let body;
      if (isEmail) body = { subject: bulkSubject, message: bulkMessage };
      else if (isWa) body = {
        channel: 'whatsapp', message: `[template:${waTemplate}]`,
        template_name: waTemplate, template_language: selectedWaTemplate?.language,
        template_params: waVars,
        header_media: ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedWaTemplate?.header_format)
          ? { type: selectedWaTemplate.header_format.toLowerCase(), link: waHeaderUrl.trim() } : undefined,
      };
      else body = { message: bulkPlain, channel: bulkChannel };
      if (selected.size > 0) body.emails = Array.from(selected);
      else Object.assign(body, activeFilters());
      if (selectedEvents.size > 0) body.event_ids = Array.from(selectedEvents);
      const url = isEmail ? '/admin/crm/email-bulk' : '/admin/crm/sms-bulk';
      const res = await api.post(url, body);
      const bits = [];
      if (res.data.no_number) bits.push(`${res.data.no_number} skipped — no number`);
      if (res.data.duplicates) bits.push(`${res.data.duplicates} duplicate number(s) removed`);
      const extra = bits.length ? ` (${bits.join('; ')})` : '';
      toast({ title: 'Queued for sending', description: `${res.data.queued} recipient(s)${extra} — sending in the background.` });
      setBulkOpen(false); setBulkSubject(''); setBulkMessage(''); setBulkPlain(''); setSelectedEvents(new Set());
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setBulkSending(false); }
  };

  const statusParams = () => {
    if (statusF === 'acct_active') return { acct_status: 'active' };
    if (statusF === 'acct_deactivated') return { acct_status: 'deactivated' };
    if (statusF === 'mem_active') return { mem_status: 'active' };
    if (statusF === 'mem_inactive') return { mem_status: 'inactive' };
    return {};
  };
  const activeFilters = () => ({
    ...(q ? { q } : {}), ...(segment ? { segment } : {}), ...(tag ? { tag } : {}),
    ...(country ? { country } : {}), ...(stateF ? { state: stateF } : {}), ...(city ? { city } : {}),
    ...statusParams(),
  });

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/crm/customers', { params: { ...activeFilters(), ...(showArchived ? { archived: true } : {}) } });
      setData(res.data || { customers: [], summary: {}, all_tags: [], facets: {} });
    } catch (e) { console.error('Failed to load customers:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCustomers(); /* eslint-disable-next-line */ }, [segment, tag, country, statusF, showArchived]);

  const s = data.summary || {};
  const facets = data.facets || { countries: [], states: [], cities: [] };
  const shownEmails = (data.customers || []).map((c) => c.email);
  const isDeletable = (c) => !c.has_account && (c.bookings_count + c.orders_count + c.donations_count) === 0;
  const deletableSelected = (data.customers || []).filter((c) => selected.has(c.email) && isDeletable(c)).map((c) => c.email);
  const archivableSelected = (data.customers || []).filter((c) => selected.has(c.email) && !c.has_account).map((c) => c.email);
  const deletableUsersSelected = (data.customers || []).filter((c) => selected.has(c.email) && c.has_account && c.user_id && !(c.roles || []).includes('admin')).map((c) => c.user_id);
  const allSelected = shownEmails.length > 0 && shownEmails.every((e) => selected.has(e));
  const toggleOne = (email, e) => {
    e.stopPropagation();
    setSelected((prev) => { const n = new Set(prev); n.has(email) ? n.delete(email) : n.add(email); return n; });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allSelected) shownEmails.forEach((e) => n.delete(e));
      else shownEmails.forEach((e) => n.add(e));
      return n;
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-customers-page">
      <div>
        <div className="eyebrow mb-2">CRM</div>
        <h1 className="font-serif-display text-4xl text-burgundy">Customers</h1>
        <p className="text-mahogany/70 text-sm mt-1">Everyone who's registered or transacted — with their full history, notes, tags and one-click email.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={Users} label="Total" value={s.total_customers || 0} testid="crm-stat-total" />
        <Stat icon={UserCheck} label="Registered" value={s.with_account || 0} testid="crm-stat-account" />
        <Stat icon={UserPlus} label="Guests" value={s.guests || 0} testid="crm-stat-guests" />
        <Stat icon={DollarSign} label="Lifetime value" value={`$${(s.total_ltv || 0).toLocaleString()}`} testid="crm-stat-ltv" />
        <Stat icon={AlertTriangle} label="Needs attention" value={s.needs_attention || 0} tone="amber" testid="crm-stat-attention" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={(e) => { e.preventDefault(); fetchCustomers(); }} className="flex items-center bg-white/70 rounded-full border border-rose/50 px-4 py-2 gap-2">
          <Search className="w-4 h-4 text-mahogany/70" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…"
            data-testid="customers-search" className="bg-transparent focus:outline-none text-burgundy w-64" />
        </form>
        <div className="flex items-center gap-1 flex-wrap">
          {SEGMENTS.map(([k, label]) => (
            <button key={k} onClick={() => setSegment(k)} data-testid={`segment-${k || 'all'}`}
              className={`px-3.5 py-1.5 rounded-full text-sm transition-colors ${segment === k ? 'bg-burgundy text-cream' : 'bg-white/70 text-mahogany hover:bg-rose-soft/50'}`}>
              {label}
            </button>
          ))}
        </div>
        {(data.all_tags || []).length > 0 && (
          <select value={tag} onChange={(e) => setTag(e.target.value)} data-testid="tag-filter"
            className="bg-white/70 rounded-full border border-rose/50 px-4 py-2 text-burgundy focus:outline-none text-sm">
            <option value="">All tags</option>
            {data.all_tags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setBulkOpen(true)} data-testid="email-filter-btn"
            className="pill-btn text-sm inline-flex items-center gap-1.5"><Send className="w-4 h-4" /> {selected.size > 0 ? `Email selected (${selected.size})` : 'Email these'}</button>
          <button onClick={saveSegment} data-testid="save-segment-btn"
            className="pill-btn-outline text-sm inline-flex items-center gap-1.5"><BookmarkPlus className="w-4 h-4" /> Save segment</button>
          <button onClick={exportCsv} data-testid="export-csv-btn"
            className="pill-btn-outline text-sm inline-flex items-center gap-1.5"><Download className="w-4 h-4" /> Export CSV</button>
          <button onClick={() => setImportOpen(true)} data-testid="import-contacts-btn"
            className="pill-btn-outline text-sm inline-flex items-center gap-1.5"><UserPlus className="w-4 h-4" /> Import</button>
          <button onClick={() => { setSelected(new Set()); setShowArchived((v) => !v); }} data-testid="toggle-archived"
            className={`text-sm inline-flex items-center gap-1.5 px-4 py-2 rounded-full transition-colors ${showArchived ? 'bg-burgundy text-cream' : 'pill-btn-outline'}`}>
            <Archive className="w-4 h-4" /> {showArchived ? 'Viewing archived' : 'Archived'}</button>
          <button onClick={loadCampaigns} data-testid="campaigns-btn"
            className="pill-btn-outline text-sm inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> Campaigns</button>
          <Link to="/dashboard/admin/inbox" data-testid="inbox-link"
            className="pill-btn-outline text-sm inline-flex items-center gap-1.5"><MessageCircle className="w-4 h-4" /> Inbox</Link>
          <button onClick={openAutomations} data-testid="automations-btn"
            className="pill-btn-outline text-sm inline-flex items-center gap-1.5"><Repeat className="w-4 h-4" /> Automations</button>
          <button onClick={() => setBouncesOpen(true)} data-testid="bounces-btn"
            className="pill-btn-outline text-sm inline-flex items-center gap-1.5"><MailWarning className="w-4 h-4" /> Bounced</button>
        </div>
      </div>

      {/* Location & status filters */}
      <div className="flex flex-wrap items-center gap-2" data-testid="crm-filter-bar">
        <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="filter-country"
          className="bg-white/70 rounded-full border border-rose/50 px-4 py-2 text-burgundy focus:outline-none text-sm">
          <option value="">All countries</option>
          {(facets.countries || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input value={stateF} onChange={(e) => setStateF(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') fetchCustomers(); }} placeholder="State/region…"
          data-testid="filter-state" className="bg-white/70 rounded-full border border-rose/50 px-4 py-2 text-burgundy focus:outline-none text-sm w-40" />
        <input value={city} onChange={(e) => setCity(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') fetchCustomers(); }} placeholder="City…"
          data-testid="filter-city" className="bg-white/70 rounded-full border border-rose/50 px-4 py-2 text-burgundy focus:outline-none text-sm w-40" />
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="filter-status"
          className="bg-white/70 rounded-full border border-rose/50 px-4 py-2 text-burgundy focus:outline-none text-sm">
          <option value="">Any status</option>
          <option value="acct_active">Account: Active</option>
          <option value="acct_deactivated">Account: Deactivated</option>
          <option value="mem_active">Membership: Active</option>
          <option value="mem_inactive">Membership: Inactive</option>
        </select>
        {(city || stateF) && (
          <button onClick={fetchCustomers} data-testid="apply-filters"
            className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors">Apply</button>
        )}
        {selected.size > 0 && (
          <button onClick={() => setSelected(new Set())} data-testid="clear-selection"
            className="text-xs px-3 py-1.5 rounded-full bg-rose-soft text-mahogany hover:bg-rose transition-colors">Clear selection ({selected.size})</button>
        )}
        {selected.size > 0 && (
          <button onClick={() => setTagOpen(true)} data-testid="bulk-tag-btn"
            className="text-xs px-3 py-1.5 rounded-full bg-burgundy/10 text-burgundy hover:bg-burgundy/20 transition-colors inline-flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5" /> Tag selected ({selected.size})
          </button>
        )}
        {deletableSelected.length > 0 && !showArchived && (
          <button onClick={() => deleteContacts(deletableSelected)} disabled={deleting} data-testid="bulk-delete-contacts"
            className="text-xs px-3 py-1.5 rounded-full bg-red-100 text-red-800 hover:bg-red-200 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60">
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete imported ({deletableSelected.length})
          </button>
        )}
        {deletableUsersSelected.length > 0 && !showArchived && (
          <button onClick={() => deleteUsers(deletableUsersSelected)} disabled={deleting} data-testid="bulk-delete-users"
            className="text-xs px-3 py-1.5 rounded-full bg-red-100 text-red-800 hover:bg-red-200 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60">
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete users ({deletableUsersSelected.length})
          </button>
        )}
        {archivableSelected.length > 0 && !showArchived && (
          <button onClick={() => archiveContacts(archivableSelected, true)} data-testid="bulk-archive-contacts"
            className="text-xs px-3 py-1.5 rounded-full bg-mahogany/10 text-mahogany hover:bg-mahogany/20 transition-colors inline-flex items-center gap-1.5">
            <Archive className="w-3.5 h-3.5" /> Archive ({archivableSelected.length})
          </button>
        )}
        {archivableSelected.length > 0 && showArchived && (
          <button onClick={() => archiveContacts(archivableSelected, false)} data-testid="bulk-restore-contacts"
            className="text-xs px-3 py-1.5 rounded-full bg-green-100 text-green-800 hover:bg-green-200 transition-colors inline-flex items-center gap-1.5">
            <ArchiveRestore className="w-3.5 h-3.5" /> Restore ({archivableSelected.length})
          </button>
        )}
      </div>

      {segments.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="saved-segments">
          <span className="text-xs uppercase tracking-widest text-mahogany/50 inline-flex items-center gap-1"><Bookmark className="w-3.5 h-3.5" /> Segments</span>
          {segments.map((sg) => (
            <button key={sg.id} onClick={() => applySegment(sg)} data-testid={`segment-chip-${sg.id}`}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/70 border border-rose/50 text-burgundy hover:bg-rose-soft/50 transition-colors">
              {sg.name}
              <span onClick={(e) => deleteSegment(sg.id, e)} data-testid={`delete-segment-${sg.id}`} className="hover:text-red-600"><X className="w-3 h-3" /></span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white/70 rounded-2xl overflow-hidden border border-rose/50">
            <thead className="bg-rose-soft/60">
              <tr className="text-left text-xs uppercase tracking-widest text-mahogany">
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all"
                    className="w-4 h-4 accent-burgundy align-middle" aria-label="Select all" />
                </th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Country</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Activity</th>
                <th className="px-4 py-3">Lifetime value</th>
                <th className="px-4 py-3">Last seen</th>
                <th className="px-4 py-3">Tags</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="customers-table-body">
              {data.customers.map((c) => (
                <tr key={c.email} onClick={() => setOpenKey(c.user_id || c.email)}
                  data-testid={`customer-row-${c.email}`}
                  className={`border-t border-rose/40 hover:bg-rose-soft/20 cursor-pointer ${selected.has(c.email) ? 'bg-rose-soft/30' : ''}`}>
                  <td className="px-4 py-3" onClick={(e) => toggleOne(c.email, e)}>
                    <input type="checkbox" checked={selected.has(c.email)} readOnly data-testid={`select-${c.email}`}
                      className="w-4 h-4 accent-burgundy align-middle" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-burgundy font-medium">{c.name}</div>
                    <div className="text-xs text-mahogany/70">{dispEmail(c.email) || <span className="italic">phone only</span>}</div>
                  </td>
                  <td className="px-4 py-3">
                    {c.has_account
                      ? <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-burgundy/10 text-burgundy">{tierLabels[c.tier] || 'Member'}</span>
                      : <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-cream-2 text-mahogany">Guest</span>}
                    {c.has_account && c.is_active === false && <span className="ml-1 text-[10px] px-2 py-1 rounded-full bg-red-100 text-red-800">Deactivated</span>}
                    {c.needs_attention > 0 && <span className="ml-1 text-[10px] px-2 py-1 rounded-full bg-amber-100 text-amber-800">{c.needs_attention} unpaid</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-mahogany/80 whitespace-nowrap" data-testid={`row-country-${c.email}`}>
                    {c.country || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-mahogany/80 whitespace-nowrap">
                    {[c.city, c.state].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-mahogany/80">
                    {c.bookings_count}× bookings · {c.orders_count}× orders · {c.donations_count}× gifts
                  </td>
                  <td className="px-4 py-3 font-medium text-burgundy">{sym(c.currency)}{Number(c.lifetime_value).toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-mahogany/80 whitespace-nowrap">{fmt(c.last_activity)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(c.tags || []).slice(0, 3).map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-rose-soft text-mahogany">{t}</span>)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {!c.has_account ? (
                      <div className="flex items-center justify-end gap-2">
                        {showArchived ? (
                          <button onClick={() => archiveContacts([c.email], false)} data-testid={`restore-contact-${c.email}`}
                            title="Restore contact" className="text-mahogany/50 hover:text-green-700"><ArchiveRestore className="w-4 h-4" /></button>
                        ) : (
                          <button onClick={() => archiveContacts([c.email], true)} data-testid={`archive-contact-${c.email}`}
                            title="Archive contact" className="text-mahogany/50 hover:text-mahogany"><Archive className="w-4 h-4" /></button>
                        )}
                        {isDeletable(c) && (
                          <button onClick={() => deleteContacts([c.email])} disabled={deleting} data-testid={`delete-contact-${c.email}`}
                            title="Delete imported contact" className="text-mahogany/50 hover:text-red-600 disabled:opacity-50"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    ) : (c.user_id && !(c.roles || []).includes('admin')) ? (
                      <button onClick={() => deleteUsers([c.user_id])} disabled={deleting} data-testid={`delete-user-${c.email}`}
                        title="Delete user (frees seats, archives orders/bookings/payments)"
                        className="text-mahogany/50 hover:text-red-600 disabled:opacity-50"><Trash2 className="w-4 h-4" /></button>
                    ) : <span className="text-mahogany/30">—</span>}
                  </td>
                </tr>
              ))}
              {data.customers.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-mahogany/60">No customers found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {openKey && <CustomerDrawer ckey={openKey} onClose={() => setOpenKey(null)} onChanged={fetchCustomers} toast={toast} tierLabels={tierLabels} />}

      <ContactImportModal open={importOpen} onClose={() => setImportOpen(false)} onImported={fetchCustomers} />
      <BounceManager open={bouncesOpen} onClose={() => setBouncesOpen(false)} onChanged={fetchCustomers} />

      {/* Bulk tag editor */}
      {tagOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="bulk-tag-modal">
          <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-serif-display text-2xl text-burgundy">Tag {selected.size} contact(s)</h3>
            <p className="text-xs text-mahogany/70 -mt-2">Add and/or remove tags across everyone you've selected — great for building lists.</p>
            <datalist id="all-tags-datalist">{(data.all_tags || []).map((t) => <option key={t} value={t} />)}</datalist>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-mahogany/60">Add tags (comma-separated)</label>
              <input value={addTags} onChange={(e) => setAddTags(e.target.value)} list="all-tags-datalist" data-testid="bulk-add-tags"
                placeholder="e.g. VIP, 2026 retreat" className="w-full mt-1 bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
            </div>
            {(data.all_tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {(data.all_tags).slice(0, 12).map((t) => (
                  <button key={t} type="button" data-testid={`quick-tag-${t}`}
                    onClick={() => setAddTags((a) => (a.split(',').map((x) => x.trim()).filter(Boolean).includes(t) ? a : (a.trim() ? `${a}, ${t}` : t)))}
                    className="text-[11px] px-2 py-1 rounded-full bg-rose-soft text-mahogany hover:bg-rose transition-colors">+ {t}</button>
                ))}
              </div>
            )}
            <div>
              <label className="text-[11px] uppercase tracking-widest text-mahogany/60">Remove tags (comma-separated)</label>
              <input value={removeTags} onChange={(e) => setRemoveTags(e.target.value)} list="all-tags-datalist" data-testid="bulk-remove-tags"
                placeholder="tag to remove" className="w-full mt-1 bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setTagOpen(false)} data-testid="bulk-tag-cancel"
                className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
              <button onClick={applyBulkTags} disabled={taggingBusy} data-testid="bulk-tag-apply"
                className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                {taggingBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />} Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk email (segment / current filter) */}
      {bulkOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="bulk-email-modal">
          <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
            <h3 className="font-serif-display text-2xl text-burgundy px-6 pt-6 pb-3 shrink-0">{selected.size > 0 ? `Message ${selected.size} selected` : 'Message this list'}</h3>

            <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4" data-testid="bulk-modal-body">
            {/* Channel selector */}
            <div className="flex gap-2" data-testid="bulk-channel-selector">
              {[['email', 'Email'], ['sms', 'SMS'], ['whatsapp', 'WhatsApp']].map(([ch, label]) => {
                const disabled = ch !== 'email' && !msgConfig[ch];
                return (
                  <button key={ch} disabled={disabled} onClick={() => setBulkChannel(ch)} data-testid={`bulk-channel-${ch}`}
                    title={disabled ? `${label} not configured yet` : ''}
                    className={`px-4 py-1.5 rounded-full text-sm transition-colors ${bulkChannel === ch ? 'bg-burgundy text-cream' : 'bg-white text-mahogany border border-rose/50 hover:bg-rose-soft/40'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                    {label}
                  </button>
                );
              })}
            </div>

            <p className="text-xs text-mahogany/70">
              {bulkChannel === 'email'
                ? (selected.size > 0 ? `Emails the ${selected.size} customer(s) you ticked.` : `Emails everyone matching the current filter (${data.customers.length} shown). Guests without an email are skipped.`)
                : (selected.size > 0 ? `Sends a ${bulkChannel === 'whatsapp' ? 'WhatsApp' : 'text'} to the ${selected.size} selected — those without a number are skipped.` : `Sends a ${bulkChannel === 'whatsapp' ? 'WhatsApp' : 'text'} to everyone matching the filter who has a phone number on file.`)}
            </p>

            {bulkChannel === 'email' ? (
              <>
                <input value={bulkSubject} onChange={(e) => setBulkSubject(e.target.value)} placeholder="Subject (you can use {{name}} here too)"
                  data-testid="bulk-subject" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
                <div data-testid="bulk-message">
                  <RichEmailEditor value={bulkMessage} onChange={setBulkMessage} placeholder="Write your message…" testid="bulk-rich-editor" />
                </div>
              </>
            ) : bulkChannel === 'whatsapp' ? (
              <div className="space-y-3" data-testid="wa-template-picker">
                <label className="block">
                  <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/70 mb-1.5">WhatsApp template</span>
                  <select value={waTemplate} onChange={(e) => onPickTemplate(e.target.value)} data-testid="wa-template-select"
                    className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany">
                    {waTemplates.length === 0 && <option value="">No approved templates found</option>}
                    {waTemplates.map((t) => (
                      <option key={t.name} value={t.name}>{t.name} ({t.language}) · {t.variables} field{t.variables === 1 ? '' : 's'}</option>
                    ))}
                  </select>
                  <span className="block text-[11px] text-mahogany/60 mt-1">WhatsApp requires a Meta-approved template. Manage templates in your Zernio dashboard.</span>
                </label>

                {(selectedWaTemplate?.variables || 0) > 0 && (
                  <div className="space-y-2" data-testid="wa-var-fields">
                    {eventOptions.length > 0 && (
                      <label className="block rounded-xl border border-rose/40 bg-rose-soft/20 p-2.5" data-testid="wa-event-attach">
                        <span className="block text-[11px] uppercase tracking-[0.12em] text-mahogany/70 mb-1">Attach a retreat / event <span className="normal-case text-mahogany/50">(fills fields with one tap)</span></span>
                        <select value={waEvent} onChange={(e) => onPickEvent(e.target.value)} data-testid="wa-event-select"
                          className="w-full bg-white rounded-lg border border-rose/50 px-2.5 py-1.5 text-sm text-burgundy focus:outline-none">
                          <option value="">— none —</option>
                          {eventOptions.map((ev) => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
                        </select>
                        {selectedWaEvent && (
                          <button type="button" onClick={fillAllFromEvent} data-testid="wa-fill-all"
                            className="mt-2 w-full text-xs px-3 py-1.5 rounded-lg bg-burgundy text-cream hover:bg-mahogany transition-colors inline-flex items-center justify-center gap-1.5">
                            <Zap className="w-3.5 h-3.5" /> Fill all fields from this event
                          </button>
                        )}
                      </label>
                    )}
                    {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedWaTemplate.header_format) && (
                      <div className="rounded-xl border border-burgundy/20 bg-burgundy/5 p-2.5" data-testid="wa-header-media">
                        <span className="block text-[11px] uppercase tracking-[0.12em] text-mahogany/70 mb-1">
                          Header {selectedWaTemplate.header_format.toLowerCase()} URL
                          <span className="normal-case text-mahogany/50"> · shows at the top of the message</span>
                        </span>
                        <input value={waHeaderUrl} onChange={(e) => setWaHeaderUrl(e.target.value)}
                          data-testid="wa-header-url" placeholder="https://… public image/video URL"
                          className="w-full bg-white rounded-lg border border-rose/50 px-2.5 py-1.5 text-sm text-burgundy focus:outline-none" />
                        {selectedWaTemplate.header_format === 'IMAGE' && waHeaderUrl && (
                          <img src={waHeaderUrl} alt="header preview" data-testid="wa-header-preview"
                            className="mt-2 max-h-28 rounded-lg object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                        )}
                        {selectedWaEvent && selectedWaTemplate.header_format === 'IMAGE' && (
                          <button type="button" onClick={() => setWaHeaderUrl(mediaUrl(selectedWaEvent.image))} data-testid="wa-header-use-event"
                            className="mt-1.5 text-[11px] px-2 py-0.5 rounded-full bg-burgundy/10 text-burgundy hover:bg-burgundy/20">Use event image</button>
                        )}
                      </div>
                    )}
                    {Array.from({ length: selectedWaTemplate.variables }).map((_, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                          <span className="text-xs text-mahogany/70">Variable {`{{${i + 1}}}`}</span>
                          <div className="flex flex-wrap gap-1">
                            <button type="button" data-testid={`wa-insert-name-${i}`}
                              onClick={() => setWaVar(i, (waVars[i] || '') + '{{name}}')}
                              className="text-[11px] px-2 py-0.5 rounded-full bg-rose-soft/60 text-mahogany hover:bg-rose-soft">+ Contact name</button>
                            {selectedWaEvent && (
                              <>
                                <button type="button" data-testid={`wa-insert-title-${i}`}
                                  onClick={() => setWaVar(i, selectedWaEvent.title || '')}
                                  className="text-[11px] px-2 py-0.5 rounded-full bg-burgundy/10 text-burgundy hover:bg-burgundy/20">Event title</button>
                                <button type="button" data-testid={`wa-insert-details-${i}`}
                                  onClick={() => setWaVar(i, selectedWaEvent.details || '')}
                                  className="text-[11px] px-2 py-0.5 rounded-full bg-burgundy/10 text-burgundy hover:bg-burgundy/20">Date &amp; location</button>
                                <button type="button" data-testid={`wa-insert-link-${i}`}
                                  onClick={() => setWaVar(i, selectedWaEvent.register_url || '')}
                                  className="text-[11px] px-2 py-0.5 rounded-full bg-burgundy/10 text-burgundy hover:bg-burgundy/20">Register link</button>
                              </>
                            )}
                          </div>
                        </div>
                        <input value={waVars[i] || ''} onChange={(e) => setWaVar(i, e.target.value)}
                          data-testid={`wa-var-${i}`} placeholder={`Value for {{${i + 1}}} — use {{name}} to personalize`}
                          className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
                      </div>
                    ))}
                  </div>
                )}

                {selectedWaTemplate && (
                  <div className="rounded-xl border border-green-200 bg-green-50/60 p-3" data-testid="wa-preview">
                    <span className="text-[11px] uppercase tracking-[0.15em] text-green-800/70">Preview</span>
                    <p className="text-sm text-green-950/90 whitespace-pre-wrap mt-1">{waPreview()}</p>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <textarea value={bulkPlain} onChange={(e) => setBulkPlain(e.target.value)} rows={4} maxLength={1500}
                  placeholder="Write your message… (use {{name}} to personalize)" data-testid="bulk-plain-message"
                  className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
                <div className="text-[11px] text-mahogany/60 mt-1 flex justify-between">
                  <span>Tip: keep texts short. Include opt-out (e.g. "Reply STOP").</span>
                  <span>{bulkPlain.length} chars{bulkChannel === 'sms' ? ` · ~${Math.ceil(bulkPlain.length / 160) || 1} SMS` : ''}</span>
                </div>
              </div>
            )}

            {/* Invite to events */}
            {bulkChannel !== 'whatsapp' && (
            <div className="rounded-xl border border-rose/40 bg-white/60 p-3 space-y-2" data-testid="campaign-events">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-burgundy inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Invite to events <span className="text-mahogany/50 font-normal">(optional)</span></span>
                <button type="button" onClick={applyInviteTemplate} data-testid="use-invite-template"
                  className="text-xs px-2.5 py-1 rounded-full bg-rose-soft/60 text-mahogany hover:bg-rose-soft transition-colors inline-flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> Use invite template</button>
              </div>
              {eventOptions.length === 0 ? (
                <p className="text-xs text-mahogany/60">No upcoming events to include right now.</p>
              ) : (
                <div className="max-h-44 overflow-y-auto space-y-1 pr-1" data-testid="event-options-list">
                  {eventOptions.map((ev) => (
                    <label key={ev.id} data-testid={`event-opt-${ev.id}`}
                      className="flex items-center gap-2 text-sm text-burgundy/90 cursor-pointer hover:bg-rose-soft/30 rounded-lg px-2 py-1">
                      <input type="checkbox" checked={selectedEvents.has(ev.id)} onChange={() => toggleEvent(ev.id)} data-testid={`event-check-${ev.id}`} />
                      {ev.image
                        ? <img src={mediaUrl(ev.image)} alt="" className="w-9 h-9 rounded-md object-cover flex-shrink-0" />
                        : <span className="w-9 h-9 rounded-md bg-rose-soft/50 inline-flex items-center justify-center flex-shrink-0"><Calendar className="w-4 h-4 text-mahogany/50" /></span>}
                      <span className="flex-1 leading-tight">
                        <span className="font-medium">{ev.title}</span>
                        {ev.when && <><br /><span className="text-[11px] text-mahogany/60">{ev.when}</span></>}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {selectedEvents.size > 0 && (
                <p className="text-[11px] text-mahogany/70" data-testid="event-selected-note">
                  {selectedEvents.size} event{selectedEvents.size > 1 ? 's' : ''} will be added to this {bulkChannel === 'email' ? 'email as a card with a “Register today” button' : 'message with a register link'}.
                </p>
              )}
            </div>
            )}

            {selected.size === 0 && bulkChannel === 'email' && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Clock className="w-4 h-4 text-mahogany/60" />
                <span className="text-xs text-mahogany/70">Schedule for later (optional):</span>
                <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
                  data-testid="schedule-at" className="bg-white rounded-lg border border-rose/50 px-2 py-1 text-sm text-burgundy focus:outline-none" />
                <select value={repeatEvery} onChange={(e) => setRepeatEvery(e.target.value)} data-testid="schedule-repeat"
                  className="bg-white rounded-lg border border-rose/50 px-2 py-1 text-sm text-burgundy focus:outline-none">
                  <option value="none">One-time</option>
                  <option value="daily">Repeat daily</option>
                  <option value="weekly">Repeat weekly</option>
                  <option value="monthly">Repeat monthly</option>
                </select>
              </div>
            )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-rose/40 bg-cream shrink-0">
              <button onClick={() => setBulkOpen(false)} data-testid="bulk-cancel"
                className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
              {selected.size === 0 && bulkChannel === 'email' && (
                <button onClick={scheduleCampaign} disabled={scheduling || !scheduleAt} data-testid="schedule-send"
                  title={!scheduleAt ? 'Pick a date & time to enable scheduling' : ''}
                  className="text-sm px-4 py-2 rounded-full bg-mahogany text-cream hover:bg-burgundy transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2">
                  {scheduling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />} Schedule
                </button>
              )}
              <button onClick={sendBulk} disabled={bulkSending} data-testid="bulk-send"
                className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                {bulkSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaigns: scheduled + recent sends with engagement */}
      {campaignsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="campaigns-modal">
          <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-2xl w-full p-6 space-y-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-2xl text-burgundy">Campaigns</h3>
              <button onClick={() => setCampaignsOpen(false)} data-testid="campaigns-close" className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-mahogany/60 mb-2">Scheduled</div>
              {scheduledList.filter((c) => c.status === 'scheduled').length === 0 ? (
                <p className="text-sm text-mahogany/60">No upcoming scheduled campaigns.</p>
              ) : scheduledList.filter((c) => c.status === 'scheduled').map((c) => (
                <div key={c.id} className="flex items-center justify-between p-3 rounded-xl border border-rose/40 bg-white/60 mb-2" data-testid={`scheduled-${c.id}`}>
                  <div>
                    <div className="text-sm text-burgundy font-medium">{c.subject}</div>
                    <div className="text-[11px] text-mahogany/60">{new Date(c.send_at).toLocaleString()} · {c.segment || 'all'}{c.tag ? ` · ${c.tag}` : ''}{c.repeat && c.repeat !== 'none' ? ` · repeats ${c.repeat}` : ''}</div>
                  </div>
                  <button onClick={() => cancelScheduled(c.id)} data-testid={`cancel-scheduled-${c.id}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft">Cancel</button>
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-mahogany/60 mb-2">Recent sends</div>
              {jobsList.length === 0 ? <p className="text-sm text-mahogany/60">No campaigns sent yet.</p> : jobsList.map((j) => (
                <div key={j.id} className="p-3 rounded-xl border border-rose/40 bg-white/60 mb-2" data-testid={`job-${j.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-burgundy font-medium">{j.subject}</div>
                      <div className="text-[11px] text-mahogany/60">
                        {j.kind} · {j.status} · {j.sent}/{j.total} sent
                        {j.failed > 0 && <span className="text-red-700"> · {j.failed} failed</span>}
                        {j.skipped > 0 && <span data-testid={`job-skipped-${j.id}`}> · {j.skipped} skipped{skipReasons(j)}</span>}
                        {' · '}{new Date(j.created_at).toLocaleString()}
                      </div>
                    </div>
                    <button onClick={() => loadEngagement(j.id)} data-testid={`engagement-${j.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-rose-soft text-mahogany hover:bg-rose">Engagement</button>
                  </div>
                  {(j.errors || []).length > 0 && (
                    <div className="mt-2 rounded-lg bg-red-50 border border-red-200 p-2" data-testid={`job-errors-${j.id}`}>
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-red-700 mb-1">Why some failed (from Zernio)</div>
                      <ul className="space-y-0.5">
                        {j.errors.slice(0, 5).map((e, i) => (
                          <li key={i} className="text-[11px] text-red-800 leading-snug"><span className="opacity-60">{e.to}:</span> {e.error}</li>
                        ))}
                        {j.errors.length > 5 && <li className="text-[10px] text-red-600">+ {j.errors.length - 5} more…</li>}
                      </ul>
                    </div>
                  )}
                  {engagement[j.id]?.open && (
                    <div className="mt-2 flex items-center gap-4 text-xs text-mahogany/80">
                      <span className="inline-flex items-center gap-1"><Send className="w-3.5 h-3.5" /> {engagement[j.id].delivered} delivered</span>
                      <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {engagement[j.id].opened} opened</span>
                      <span className="inline-flex items-center gap-1"><MousePointer className="w-3.5 h-3.5" /> {engagement[j.id].clicked} clicked</span>
                      {engagement[j.id].bounced > 0 && <span className="text-red-700">{engagement[j.id].bounced} bounced</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Automations: winback settings */}
      {autoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="automations-modal">
          <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-2xl text-burgundy inline-flex items-center gap-2"><Zap className="w-5 h-5" /> Winback automation</h3>
              <button onClick={() => setAutoOpen(false)} data-testid="automations-close" className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
            </div>
            {!winback ? <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div> : (
              <>
                <p className="text-sm text-mahogany/70">Automatically email past attendees a gentle "we miss you" note once they've been inactive for the set number of days. Each person is emailed at most once a year.</p>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={!!winback.enabled} onChange={(e) => setWinback({ ...winback, enabled: e.target.checked })}
                    data-testid="winback-enabled" className="w-4 h-4 accent-burgundy" />
                  <span className="text-sm text-burgundy">Enable winback automation</span>
                </label>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1.5">Inactive for (days)</label>
                  <input type="number" value={winback.days} onChange={(e) => setWinback({ ...winback, days: e.target.value })}
                    data-testid="winback-days" className="w-32 bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" />
                </div>
                <input value={winback.subject || ''} onChange={(e) => setWinback({ ...winback, subject: e.target.value })}
                  placeholder="Subject" data-testid="winback-subject" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
                <div data-testid="winback-message">
                  <RichEmailEditor value={winback.message} onChange={(v) => setWinback({ ...winback, message: v })} placeholder="Your we-miss-you message…" testid="winback-rich-editor" />
                </div>
                <div className="flex justify-between gap-3">
                  <button onClick={runWinbackNow} data-testid="winback-run"
                    className="text-sm px-4 py-2 rounded-full bg-rose-soft text-mahogany hover:bg-rose transition-colors">Run now</button>
                  <button onClick={saveWinback} disabled={savingWb} data-testid="winback-save"
                    className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                    {savingWb && <Loader2 className="w-4 h-4 animate-spin" />} Save settings
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CustomerDrawer = ({ ckey, onClose, onChanged, toast, tierLabels = TIER_LABELS }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [tagInput, setTagInput] = useState('');
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [compose, setCompose] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [callOpen, setCallOpen] = useState(false);

  const load = async (overrideKey) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/crm/customers/${encodeURIComponent(overrideKey || ckey)}`);
      setD(res.data);
    } catch (e) { console.error('Failed to load customer:', e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ckey]);

  const p = d?.profile;
  const m = d?.metrics || {};
  const keyForApi = p ? (p.user_id || p.email) : ckey;

  const addTag = async (t) => {
    const clean = t.trim();
    if (!clean) return;
    const next = Array.from(new Set([...(p.tags || []), clean]));
    try {
      await api.put(`/admin/crm/customers/${encodeURIComponent(keyForApi)}/tags`, { tags: next });
      setTagInput(''); load(); onChanged();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const removeTag = async (t) => {
    const next = (p.tags || []).filter((x) => x !== t);
    try { await api.put(`/admin/crm/customers/${encodeURIComponent(keyForApi)}/tags`, { tags: next }); load(); onChanged(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };

  const resubscribe = async () => {
    try { await api.post(`/admin/crm/customers/${encodeURIComponent(keyForApi)}/resubscribe`); toast({ title: 'Re-subscribed' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };

  const saveNote = async () => {
    if (noteText.trim().length < 1) return;
    setSavingNote(true);
    try {
      await api.post(`/admin/crm/customers/${encodeURIComponent(keyForApi)}/notes`, { text: noteText });
      setNoteText(''); load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
    finally { setSavingNote(false); }
  };
  const deleteNote = async (id) => {
    try { await api.delete(`/admin/crm/notes/${id}`); load(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };
  const deleteCall = async (id) => {
    try { await api.delete(`/admin/crm/calls/${id}`); load(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
  };

  const sendEmail = async () => {
    if (subject.trim().length < 1 || richTextIsEmpty(message)) { toast({ title: 'Add a subject and message.' }); return; }
    setSending(true);
    try {
      await api.post('/admin/crm/email', { email: p.email, subject, message, user_id: p.user_id || null });
      toast({ title: 'Email sent', description: p.email });
      setCompose(false); setSubject(''); setMessage(''); load();
    } catch (e) { toast({ title: 'Could not send', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setSending(false); }
  };

  const openEdit = () => {
    setForm({
      name: p.name || '', email: dispEmail(p.email) || '', phone: p.phone || '', whatsapp: p.whatsapp || '',
      street1: p.street1 || '', street2: p.street2 || '', zip: p.zip || '',
      city: p.city || '', state: p.state || '', country: p.country || '',
      alt_emails: p.alt_emails || '', alt_phones: p.alt_phones || '',
      lead_source: p.lead_source || '', contact_notes: p.contact_notes || '',
    });
    setEditing(true);
  };
  const saveDetails = async () => {
    if (!(form.name || '').trim()) { toast({ title: 'Name is required.' }); return; }
    const hadRealEmail = !!dispEmail(p.email) || p.has_account;
    if (hadRealEmail && !(form.email || '').trim()) { toast({ title: 'Email is required.' }); return; }
    setSavingEdit(true);
    try {
      const res = await api.put(`/admin/crm/customers/${encodeURIComponent(keyForApi)}/details`, form);
      toast({ title: 'Details saved' });
      setEditing(false);
      onChanged();
      const newKey = (!p.user_id && res.data?.email) ? res.data.email : ckey;
      load(newKey);
    } catch (e) { toast({ title: 'Could not save', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setSavingEdit(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="customer-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-cream h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-cream/95 backdrop-blur border-b border-rose/40 px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <h2 className="font-serif-display text-2xl text-burgundy truncate" data-testid="drawer-name">{p?.name || '…'}</h2>
            <div className="text-xs text-mahogany/70 truncate">{dispEmail(p?.email) || 'Phone-only contact'}</div>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
        </div>

        {loading || !p ? (
          <div className="p-6 flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="p-6 space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              {p.has_account
                ? <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-burgundy/10 text-burgundy">{tierLabels[p.tier] || 'Member'}</span>
                : <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-cream-2 text-mahogany">Guest (no account)</span>}
              {p.is_active === false && <span className="text-[10px] px-2 py-1 rounded-full bg-red-100 text-red-800">Disabled</span>}
              {p.unsubscribed && (
                <span className="text-[10px] px-2 py-1 rounded-full bg-mahogany/15 text-mahogany inline-flex items-center gap-1" data-testid="unsub-badge">
                  Unsubscribed
                  <button onClick={resubscribe} data-testid="resubscribe-btn" className="underline hover:text-burgundy">re-subscribe</button>
                </span>
              )}
              <button onClick={openEdit} data-testid="drawer-edit-btn"
                className="ml-auto pill-btn text-xs inline-flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Edit details</button>
              <button onClick={() => setCompose(true)} data-testid="drawer-email-btn"
                className="pill-btn text-xs inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Email</button>
              <button onClick={() => setCallOpen(true)} data-testid="drawer-logcall-btn"
                className="pill-btn-outline text-xs inline-flex items-center gap-1.5"><PhoneCall className="w-3.5 h-3.5" /> Log call</button>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <Mini label="LTV" value={`$${(m.lifetime_value || 0).toLocaleString()}`} />
              <Mini label="Bookings" value={m.bookings_count || 0} />
              <Mini label="Orders" value={m.orders_count || 0} />
              <Mini label="Gifts" value={m.donations_count || 0} />
            </div>

            {/* Contact details (read-only summary) */}
            <div className="rounded-2xl border border-rose/40 bg-white/60 p-4" data-testid="drawer-contact">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-mahogany/70"><Phone className="w-3.5 h-3.5" /> Contact details</div>
                <button onClick={openEdit} data-testid="edit-details-inline" className="text-xs inline-flex items-center gap-1 text-burgundy hover:text-mahogany"><Pencil className="w-3.5 h-3.5" /> Edit</button>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <Field label="Phone" value={p.phone} testid="contact-phone" />
                <Field label="WhatsApp" value={p.whatsapp} testid="contact-whatsapp" />
                <Field label="Alt emails" value={p.alt_emails} testid="contact-alt-emails" />
                <Field label="Alt phones" value={p.alt_phones} testid="contact-alt-phones" />
                <Field label="Address" value={[p.street1, p.street2].filter(Boolean).join(', ')} testid="contact-address" />
                <Field label="City" value={p.city} testid="contact-city" />
                <Field label="State" value={p.state} testid="contact-state" />
                <Field label="Zip" value={p.zip} testid="contact-zip" />
                <Field label="Country" value={p.country} testid="contact-country" />
                <Field label="Lead source" value={p.lead_source} testid="contact-source" />
              </div>
              {p.contact_notes && (
                <div className="mt-3 pt-3 border-t border-rose/30" data-testid="contact-notes">
                  <div className="text-[11px] uppercase tracking-widest text-mahogany/60 mb-1">Notes</div>
                  <p className="text-sm text-burgundy/90 whitespace-pre-wrap">{p.contact_notes}</p>
                </div>
              )}
            </div>

            {/* Tags */}
            <div>
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-mahogany/70 mb-2"><Tag className="w-3.5 h-3.5" /> Tags</div>
              <div className="flex flex-wrap gap-2 items-center" data-testid="drawer-tags">
                {(p.tags || []).map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-rose-soft text-mahogany">
                    {t}<button onClick={() => removeTag(t)} data-testid={`remove-tag-${t}`} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                  </span>
                ))}
                <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); } }}
                  placeholder="+ tag" data-testid="tag-input"
                  className="text-xs bg-white rounded-full border border-rose/50 px-3 py-1 w-24 focus:outline-none focus:border-mahogany" />
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-2 border-b border-rose/40">
              {[['overview', 'Activity'], ['notes', `Notes (${(d.notes || []).length})`]].map(([k, label]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`drawer-tab-${k}`}
                  className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === k ? 'border-burgundy text-burgundy font-medium' : 'border-transparent text-mahogany/60 hover:text-mahogany'}`}>
                  {label}
                </button>
              ))}
            </div>

            {tab === 'overview' ? (
              <div className="space-y-2" data-testid="drawer-timeline">
                {(d.timeline || []).length === 0 && <div className="text-sm text-mahogany/60">No activity yet.</div>}
                {(d.timeline || []).map((ev, i) => {
                  const Icon = ev.type === 'message' ? (CHANNEL_ICON[ev.channel] || MessageSquare) : (TYPE_ICON[ev.type] || Calendar);
                  return (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-rose/40 bg-white/60">
                      <Icon className="w-4 h-4 text-mahogany/70 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm text-burgundy font-medium truncate">{ev.title}</span>
                          {ev.amount != null && <span className="text-sm text-burgundy whitespace-nowrap">{sym(ev.currency)}{Number(ev.amount).toFixed(2)}</span>}
                          {ev.type === 'call' && (
                            <button onClick={() => deleteCall(ev.ref)} data-testid={`delete-call-${ev.ref}`} className="text-mahogany/50 hover:text-red-600 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                        {ev.type === 'message' && ev.text && (
                          <p className="text-xs text-mahogany/80 mt-1 line-clamp-2" data-testid="timeline-msg-text">{ev.text}</p>
                        )}
                        {ev.type === 'call' && ev.text && (
                          <p className="text-xs text-mahogany/80 mt-1 whitespace-pre-wrap" data-testid="timeline-call-notes">{ev.text}</p>
                        )}
                        {ev.type === 'message' && ev.error && (
                          <p className="text-xs text-red-600 mt-1">{ev.error}</p>
                        )}
                        <div className="flex items-center flex-wrap gap-2 mt-0.5">
                          {ev.type === 'call' && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
                              {ev.channel === 'whatsapp' ? 'WhatsApp' : 'Phone'} · {ev.direction === 'inbound' ? 'Inbound' : 'Outbound'}{ev.duration_min ? ` · ${ev.duration_min}m` : ''}
                            </span>
                          )}
                          {ev.type === 'message' && ev.channel && ev.channel !== 'email' && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-800">{CHANNEL_LABEL[ev.channel]}</span>
                          )}
                          {ev.type === 'message' && ev.direction === 'in' && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">↩ Reply</span>
                          )}
                          <span className="text-[11px] text-mahogany/60">{fmt(ev.date)}</span>
                          {ev.status && <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE[ev.status] || 'bg-cream-2 text-mahogany'}`}>{String(ev.status).replace('_', ' ')}</span>}
                          {ev.checked_in && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-800">Checked in</span>}
                          {ev.opened && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Opened</span>}
                          {ev.clicked && <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">Clicked</span>}
                          {ev.sent_by && <span className="text-[10px] text-mahogany/50">by {ev.sent_by}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-3" data-testid="drawer-notes">
                <div className="flex items-start gap-2">
                  <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} placeholder="Add a private note…"
                    data-testid="note-input" className="flex-1 bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
                  <button onClick={saveNote} disabled={savingNote} data-testid="note-add"
                    className="pill-btn text-xs shrink-0 mt-0.5">{savingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add'}</button>
                </div>
                {(d.notes || []).map((n) => (
                  <div key={n.id} className="p-3 rounded-xl border border-rose/40 bg-white/60">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-burgundy/90">{n.text}</p>
                      <button onClick={() => deleteNote(n.id)} data-testid={`delete-note-${n.id}`} className="text-mahogany/50 hover:text-red-600 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="text-[11px] text-mahogany/50 mt-1">{n.author_name} · {fmt(n.created_at)}</div>
                  </div>
                ))}
                {(d.notes || []).length === 0 && <div className="text-sm text-mahogany/60">No notes yet.</div>}
              </div>
            )}
          </div>
        )}

        {/* Edit details */}
        {editing && p && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="edit-details-modal">
            <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-lg w-full p-6 space-y-3 max-h-[90vh] overflow-y-auto">
              <h3 className="font-serif-display text-2xl text-burgundy">Edit details</h3>
              <div className="grid grid-cols-2 gap-3">
                <EditField label="Name" testid="edit-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} full />
                <div className="col-span-2">
                  <EditField label="Email" testid="edit-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} full />
                  {p.has_account && (form.email || '').trim().toLowerCase() !== (p.email || '').toLowerCase() && (
                    <p className="text-[11px] text-amber-700 mt-1" data-testid="edit-email-warn">This is the member's login email — changing it updates how they sign in.</p>
                  )}
                </div>
                <EditField label="Phone" testid="edit-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                <EditField label="WhatsApp" testid="edit-whatsapp" value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} />
                <EditField label="Alt emails" testid="edit-alt-emails" value={form.alt_emails} onChange={(v) => setForm({ ...form, alt_emails: v })} placeholder="comma-separated" />
                <EditField label="Alt phones" testid="edit-alt-phones" value={form.alt_phones} onChange={(v) => setForm({ ...form, alt_phones: v })} placeholder="comma-separated" />
                <EditField label="Street address 1" testid="edit-street1" value={form.street1} onChange={(v) => setForm({ ...form, street1: v })} full />
                <EditField label="Street address 2" testid="edit-street2" value={form.street2} onChange={(v) => setForm({ ...form, street2: v })} full />
                <EditField label="City" testid="edit-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
                <EditField label="State" testid="edit-state" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
                <EditField label="Zip / Postal" testid="edit-zip" value={form.zip} onChange={(v) => setForm({ ...form, zip: v })} />
                <EditField label="Country" testid="edit-country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
                <EditField label="Lead source" testid="edit-source" value={form.lead_source} onChange={(v) => setForm({ ...form, lead_source: v })} full placeholder="e.g. Instagram, referral, FB Leads" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-mahogany/60">Notes</label>
                <textarea value={form.contact_notes} onChange={(e) => setForm({ ...form, contact_notes: e.target.value })} rows={3}
                  data-testid="edit-notes" placeholder="Internal notes about this customer…"
                  className="w-full mt-1 bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
              </div>
              <div className="flex justify-end gap-3 pt-1">
                <button onClick={() => setEditing(false)} data-testid="edit-cancel"
                  className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
                <button onClick={saveDetails} disabled={savingEdit} data-testid="edit-save"
                  className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                  {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />} Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Compose email */}
        {compose && p && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="compose-modal">
            <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-lg w-full p-6 space-y-4">
              <h3 className="font-serif-display text-2xl text-burgundy">Email {p.name}</h3>
              <div className="text-xs text-mahogany/70">To: {p.email}</div>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject"
                data-testid="compose-subject" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
              <div data-testid="compose-message">
                <RichEmailEditor value={message} onChange={setMessage} placeholder="Write your message…" testid="compose-rich-editor" />
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setCompose(false)} data-testid="compose-cancel"
                  className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
                <button onClick={sendEmail} disabled={sending} data-testid="compose-send"
                  className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
                </button>
              </div>
            </div>
          </div>
        )}

        {callOpen && p && (
          <CallModal
            name={p.name}
            defaultChannel={p.whatsapp ? 'whatsapp' : 'phone'}
            onClose={() => setCallOpen(false)}
            onSaved={() => { setCallOpen(false); load(); }}
            keyForApi={keyForApi}
            toast={toast}
          />
        )}
      </div>
    </div>
  );
};

const toneMap = { amber: 'text-amber-700' };
const Stat = ({ icon: Icon, label, value, tone, testid }) => (
  <div className="p-4 rounded-2xl border border-rose/50 bg-white/70" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-mahogany/70"><Icon className={`w-4 h-4 ${toneMap[tone] || ''}`} /> {label}</div>
    <div className="font-serif-display text-2xl text-burgundy mt-1">{value}</div>
  </div>
);
const Mini = ({ label, value }) => (
  <div className="p-2.5 rounded-xl border border-rose/40 bg-white/60 text-center">
    <div className="text-[10px] uppercase tracking-widest text-mahogany/60">{label}</div>
    <div className="text-lg text-burgundy font-medium">{value}</div>
  </div>
);
const Field = ({ label, value, testid }) => (
  <div>
    <div className="text-[11px] uppercase tracking-widest text-mahogany/60">{label}</div>
    <div className="text-sm text-burgundy" data-testid={testid}>{value || <span className="text-mahogany/40">—</span>}</div>
  </div>
);
const EditField = ({ label, value, onChange, testid, placeholder, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="text-[11px] uppercase tracking-widest text-mahogany/60">{label}</label>
    <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      data-testid={testid} className="w-full mt-1 bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
  </div>
);

const CALL_OUTCOMES = [
  ['connected', 'Connected'], ['no_answer', 'No answer'], ['voicemail', 'Left voicemail'],
  ['callback', 'Callback requested'], ['other', 'Other'],
];

const CallModal = ({ name, defaultChannel, onClose, onSaved, keyForApi, toast }) => {
  const [channel, setChannel] = useState(defaultChannel || 'phone');
  const [direction, setDirection] = useState('outbound');
  const [outcome, setOutcome] = useState('connected');
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const cleanWithAI = async () => {
    if (notes.trim().length < 3) { toast({ title: 'Add a few words first, then let AI tidy them.' }); return; }
    setCleaning(true);
    try {
      const res = await api.post('/admin/crm/calls/summarize', { raw: notes, name });
      if (res.data.summary) setNotes(res.data.summary);
      toast({ title: 'Notes cleaned up' });
    } catch (e) { toast({ title: 'AI cleanup failed', description: e?.response?.data?.detail }); }
    finally { setCleaning(false); }
  };

  const save = async () => {
    if (notes.trim().length < 1) { toast({ title: 'Add some notes about the call.' }); return; }
    setSaving(true);
    try {
      await api.post(`/admin/crm/customers/${encodeURIComponent(keyForApi)}/calls`, {
        channel, direction, outcome,
        duration_min: duration ? parseInt(duration, 10) : null,
        notes,
      });
      toast({ title: 'Call logged' });
      onSaved();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); setSaving(false); }
  };

  const seg = (val, cur, set, opts, tid) => (
    <div className="flex flex-wrap gap-1.5" data-testid={tid}>
      {opts.map(([v, label]) => (
        <button key={v} type="button" onClick={() => set(v)}
          className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${cur === v ? 'bg-mahogany text-cream border-mahogany' : 'bg-white text-mahogany border-rose/50 hover:bg-rose-soft/40'}`}
          data-testid={`${tid}-${v}`}>{label}</button>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="call-modal">
      <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-2xl text-burgundy flex items-center gap-2"><PhoneCall className="w-5 h-5" /> Log a call</h3>
          <button onClick={onClose} data-testid="call-close" className="text-mahogany/60 hover:text-burgundy"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-xs text-mahogany/70">With {name}</div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="text-[11px] uppercase tracking-widest text-mahogany/60">Type</span>
            <div className="mt-1">{seg(channel, channel, setChannel, [['phone', 'Phone'], ['whatsapp', 'WhatsApp']], 'call-channel')}</div>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-widest text-mahogany/60">Direction</span>
            <div className="mt-1">{seg(direction, direction, setDirection, [['outbound', 'Outbound'], ['inbound', 'Inbound']], 'call-direction')}</div>
          </div>
        </div>

        <div>
          <span className="text-[11px] uppercase tracking-widest text-mahogany/60">Outcome</span>
          <div className="mt-1">{seg(outcome, outcome, setOutcome, CALL_OUTCOMES, 'call-outcome')}</div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-widest text-mahogany/60">Duration (minutes)</label>
          <input type="number" min="0" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="e.g. 12"
            data-testid="call-duration" className="w-full mt-1 bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] uppercase tracking-widest text-mahogany/60">Notes</label>
            <div className="flex items-center gap-2">
              <VoiceRecorder label="Dictate" testid="call-voice" onTranscript={(t) => setNotes((n) => (n ? n + ' ' : '') + t)} />
              <button type="button" onClick={cleanWithAI} disabled={cleaning} data-testid="call-ai-clean"
                className="pill-btn-outline text-xs inline-flex items-center gap-1.5">
                {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Clean with AI
              </button>
            </div>
          </div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={7} placeholder="Type or dictate what was discussed — then tap 'Clean with AI' for a tidy summary."
            data-testid="call-notes" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany whitespace-pre-wrap" />
        </div>

        <div className="flex justify-end gap-3">
          <button onClick={onClose} data-testid="call-cancel"
            className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="call-save"
            className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />} Save call
          </button>
        </div>
      </div>
    </div>
  );
};


export default AdminCustomers;
